import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { CashRegisterDetailDto, PackageOptionDto, PaymentInput, PaymentMethodDto } from '@barbeiropro/contracts';
import { FormModal } from '@/components/shared/form-modal';
import { Field } from '@/components/shared/field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '@/lib/api';
import { money } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCreatePayment, usePaymentQuote } from '../api';
import { KIND_LABEL } from '../labels';
import { MoneyInput } from '../money-input';

export type CheckoutTarget = {
  kind: 'appointment' | 'subscription';
  appointmentId?: string;
  subscriptionId?: string;
  customerId: string;
  customerName: string;
  title: string;
  subtitle?: string;
  branchId: string | null;
};

type FormValues = { items: { paymentMethodId: string; amountCents: number }[]; notes: string };
const NO_PACKAGE = 'none';

export function CheckoutForm({
  target,
  branchId,
  register,
  methods,
  packages,
  idempotencyKey,
  onClose,
}: {
  target: CheckoutTarget;
  branchId: string | null;
  register: CashRegisterDetailDto | null;
  methods: PaymentMethodDto[];
  packages: PackageOptionDto[];
  idempotencyKey: string;
  onClose: () => void;
}) {
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState('');
  const [packageId, setPackageId] = useState<string>(NO_PACKAGE);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const pay = useCreatePayment();

  const ids = { appointmentId: target.appointmentId ?? null, subscriptionId: target.subscriptionId ?? null };
  const base = usePaymentQuote(ids, true);
  const hasMods = !!coupon || packageId !== NO_PACKAGE;
  const mods = usePaymentQuote(
    { ...ids, couponCode: coupon || undefined, usePackageSubscriptionId: packageId !== NO_PACKAGE ? packageId : undefined },
    hasMods,
  );
  const quote = hasMods && mods.data ? mods.data : base.data;
  const modsApplied = hasMods && !!mods.data;
  const total = quote?.totalCents ?? 0;

  const form = useForm<FormValues>({ defaultValues: { items: [{ paymentMethodId: methods[0]!.id, amountCents: 0 }], notes: '' } });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });
  const items = form.watch('items');

  // Com uma única linha, ela acompanha o total (cupom e pacote mudam o valor).
  useEffect(() => {
    if (form.getValues('items').length === 1) form.setValue('items.0.amountCents', total);
  }, [total]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsPayment = total > 0;
  const sum = items.reduce((s, i) => s + (i.amountCents || 0), 0);
  const remaining = needsPayment ? total - sum : 0;
  const linesValid = !needsPayment || (items.length > 0 && items.every((i) => i.paymentMethodId && i.amountCents > 0));
  const canConfirm = !!register && !!quote && !(hasMods && mods.isFetching) && remaining === 0 && linesValid && !pay.isPending;

  function applyCoupon() {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (code === coupon) void mods.refetch();
    setCoupon(code);
  }
  function clearCoupon() {
    setCoupon('');
    setCouponInput('');
  }

  async function submit(values: FormValues) {
    if (!register || !quote) return;
    setSubmitError(null);
    const payload: PaymentInput = {
      idempotencyKey,
      appointmentId: target.appointmentId ?? null,
      subscriptionId: target.subscriptionId ?? null,
      customerId: target.customerId,
      cashRegisterId: register.id,
      items: needsPayment ? values.items.map((i) => ({ paymentMethodId: i.paymentMethodId, amountCents: i.amountCents })) : [],
      couponCode: modsApplied && coupon ? coupon : undefined,
      usePackageSubscriptionId: modsApplied && packageId !== NO_PACKAGE ? packageId : undefined,
      notes: values.notes.trim() || null,
    };
    try {
      await pay.mutateAsync(payload);
      toast.success('Recebimento confirmado.');
      onClose();
    } catch (e) {
      const msg = errorMessage(e);
      setSubmitError(msg);
      toast.error(msg);
    }
  }

  const couponError = hasMods && mods.error ? errorMessage(mods.error) : null;
  const unitQuery = branchId ? `?aba=caixa&unidade=${branchId}` : '?aba=caixa';

  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      size="lg"
      title="Receber"
      description={target.kind === 'appointment' ? 'Confirme os valores e a forma de pagamento do atendimento.' : 'Confirme o pagamento da assinatura.'}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" form="checkout-form" disabled={!canConfirm}>
            {pay.isPending ? 'Confirmando…' : 'Confirmar recebimento'}
          </Button>
        </>
      }
    >
      <form id="checkout-form" onSubmit={form.handleSubmit(submit)} className="grid gap-6" noValidate>
        {!register && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brass/50 bg-brass/10 p-4 text-sm">
            <p>Não há caixa aberto nesta unidade. Abra o caixa para receber.</p>
            <Button asChild size="sm" variant="outline">
              <Link to={`/app/caixa${unitQuery}`}>Abrir caixa</Link>
            </Button>
          </div>
        )}

        <section aria-label="Resumo" className="grid gap-1 rounded-lg border bg-muted/40 p-4">
          <p className="text-base font-semibold">{target.title}</p>
          <p className="text-sm text-muted-foreground">
            Cliente: {target.customerName}
            {target.subtitle ? ` · ${target.subtitle}` : ''}
          </p>
          {base.isLoading ? (
            <p className="text-sm text-muted-foreground">Calculando valores…</p>
          ) : base.error ? (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage(base.error)}{' '}
              <button type="button" className="underline" onClick={() => void base.refetch()}>
                Tentar de novo
              </button>
            </p>
          ) : quote ? (
            <dl className="mt-2 grid gap-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Valor</dt>
                <dd className="tabular">{money(quote.grossCents)}</dd>
              </div>
              {quote.discountCents > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Desconto{quote.couponCode ? ` (${quote.couponCode})` : ''}</dt>
                  <dd className="tabular text-primary">-{money(quote.discountCents)}</dd>
                </div>
              )}
              {quote.coveredByPackage && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Coberto pelo pacote do cliente</dt>
                  <dd className="tabular">Sem cobrança</dd>
                </div>
              )}
              <div className="flex justify-between border-t pt-1 text-base font-semibold">
                <dt>Total a receber</dt>
                <dd className="tabular">{money(quote.totalCents)}</dd>
              </div>
            </dl>
          ) : null}
        </section>

        <section aria-label="Cupom" className="grid gap-1.5">
          <Field id="coupon" label="Cupom de desconto" error={couponError ?? undefined}>
            <div className="flex gap-2">
              <Input
                id="coupon"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyCoupon();
                  }
                }}
                maxLength={30}
                placeholder="Digite o código"
                aria-invalid={!!couponError}
                aria-describedby={couponError ? 'coupon-error' : undefined}
              />
              <Button type="button" variant="outline" onClick={applyCoupon} disabled={!couponInput.trim() || mods.isFetching}>
                Aplicar cupom
              </Button>
              {coupon && (
                <Button type="button" variant="ghost" onClick={clearCoupon}>
                  Remover cupom
                </Button>
              )}
            </div>
          </Field>
        </section>

        {packages.length > 0 && (
          <Field id="package" label="Usar pacote do cliente" hint="Ao usar um pacote, o atendimento não gera cobrança.">
            <Select value={packageId} onValueChange={setPackageId}>
              <SelectTrigger id="package">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PACKAGE}>Não usar pacote</SelectItem>
                {packages.map((p) => (
                  <SelectItem key={p.subscriptionId} value={p.subscriptionId}>
                    {p.planName}
                    {p.usesRemaining !== null ? ` (${p.usesRemaining} ${p.usesRemaining === 1 ? 'uso restante' : 'usos restantes'})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}

        {needsPayment ? (
          <section aria-label="Pagamento" className="grid gap-3">
            <h3 className="text-sm font-semibold">Formas de pagamento</h3>
            {fields.map((f, idx) => {
              const row = items[idx];
              return (
                <div key={f.id} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_10rem_auto_auto] sm:items-end">
                  <Field id={`method-${idx}`} label={`Método ${idx + 1}`}>
                    <Controller
                      control={form.control}
                      name={`items.${idx}.paymentMethodId`}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id={`method-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {methods.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name} ({KIND_LABEL[m.kind]})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </Field>
                  <Field id={`amount-${idx}`} label="Valor">
                    <Controller control={form.control} name={`items.${idx}.amountCents`} render={({ field }) => <MoneyInput id={`amount-${idx}`} value={field.value} onChange={field.onChange} />} />
                  </Field>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={remaining <= 0}
                    onClick={() => form.setValue(`items.${idx}.amountCents`, (row?.amountCents ?? 0) + remaining, { shouldDirty: true })}
                  >
                    Preencher restante
                  </Button>
                  <Button type="button" variant="ghost" size="icon" aria-label={`Remover método ${idx + 1}`} disabled={fields.length === 1} onClick={() => remove(idx)}>
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              );
            })}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button type="button" variant="outline" size="sm" disabled={fields.length >= 5} onClick={() => append({ paymentMethodId: methods[0]!.id, amountCents: Math.max(0, remaining) })}>
                <Plus className="size-4" aria-hidden /> Adicionar método
              </Button>
              <p
                role="status"
                className={cn('text-sm font-medium tabular', remaining > 0 && 'text-amber-ink', remaining < 0 && 'text-oxblood-ink', remaining === 0 && 'text-primary')}
              >
                {remaining > 0 ? `Restante a pagar: ${money(remaining)}` : remaining < 0 ? `Valor excedido em ${money(-remaining)}` : 'Restante a pagar: R$ 0,00'}
              </p>
            </div>
            {methods.length > 0 && fields.length > 1 && (
              <p className="text-xs text-muted-foreground">Até 5 formas de pagamento por recebimento.</p>
            )}
          </section>
        ) : (
          quote && <p className="rounded-lg border bg-muted/40 p-3 text-sm">Este recebimento não tem valor a cobrar. Confirme para registrar.</p>
        )}

        <Field id="pay-notes" label="Observações (opcional)">
          <Textarea id="pay-notes" rows={2} maxLength={300} {...form.register('notes')} />
        </Field>

        {submitError && (
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {submitError} Seus dados foram mantidos: confirme de novo para tentar outra vez.
          </p>
        )}
      </form>
    </FormModal>
  );
}
